import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./Orbit2.css";
import { useAsyncFn } from "../../hooks/use-async-fn";
import config from "../../config";
import { useFederation } from "../FederationSelection/FederationContext";
import { useToast } from "../../contexts/toast-context";
import { OnamHub } from "../../onam/hub/OnamHub";
import { canCurate, CURATION_DENIED } from "../../onam/access";
const storedPermissions = JSON.parse(localStorage.getItem("userPermissions"));

const subMenus: Record<
  string,
  { title: string; text: string; route?: string; desc?: string; cta?: string }[]
> = {
  Discover: [
    {
      title: "Curation",
      route:
        storedPermissions?.createfederation === "no"
          ? "/choose-federation"
          : "/federation-selection",
      text: "",
      desc: "Build audiences from your federation's datasets.",
      cta: "Open Curation",
    },
    { title: "Insights", text: "Coming soon", desc: "See who is in the network before you buy." },
    { title: "ID Resolution", text: "Coming soon", desc: "Match identities across partners, privately." },
  ],
  Engage: [
    { title: "Loyalty program", text: "Coming soon", desc: "Reward and keep your customers with partner audiences." },
    { title: "Advertising", text: "", route: "/onam/advertising", desc: "Find, combine and send audiences to your ad platforms.", cta: "Open Advertising" },
    { title: "CRM", text: "Coming soon", desc: "Enrich your customer records with partner audiences." },
  ],
  Measure: [
    { title: "Optimization", text: "Coming soon", desc: "Shift spend to the audiences that work." },
    { title: "Attribution", text: "Coming soon", desc: "See which audiences drove results." },
    { title: "Cross Media", text: "Coming soon", desc: "Follow reach and frequency across channels." },
  ],
};

const mainCards: { title: string; text: string; desc?: string; cta?: string }[] = [
  {
    title: "Discover",
    text: "",
    desc: "Find and curate audiences across your federations.",
    cta: "Open Discover",
  },
  {
    title: "Engage",
    text: "",
    desc: "Put partner audiences to work: advertising, loyalty and CRM.",
    cta: "Open Engage",
  },
  {
    title: "Measure",
    text: "Coming soon",
    desc: "See what your campaigns achieved, partner by partner.",
  },
];

const Orbit2 = ({ authContext }) => {
  const [activeConsole, setActiveConsole] = useState<string | null>(null);
  const [cardsToShow, setCardstoShow] = useState(mainCards);
  const { appMetadata, getPermissionsbyrole } = authContext;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const consoleParam = queryParams?.get("console");
  const { setFedId, setPlatformLogo } = useFederation();
  let tenantObj = {};
  tenantObj = appMetadata && Object.values(appMetadata);
  const organizationId = tenantObj[0].organization;
  var sessionOrganizationId = organizationId;
  localStorage.setItem("organizationkey", sessionOrganizationId);

  var authRole = tenantObj[0]?.roles[0];
  localStorage.setItem("authRole", authRole);
  const getPermissionsbyroleFn = useAsyncFn({
    promiseFn: getPermissionsbyrole,
    userRole: authRole,
  });

  const requiredPages = [
    "createfederation",
    "campaignplanning",
    "query",
    "provenancetrail",
    "activationhistory",
    "activationconnectors",
    "datamanager",
  ];

  const apiPermissions = getPermissionsbyroleFn?.data?.rbacGroupName || [];

  const finalPermissions = requiredPages.map((page) => {
    const match = apiPermissions.find((perm) => perm.startsWith(`${page}_`));
    return match || `${page}_no_access`;
  });

  const permissionsObj = {};

  const roles = getPermissionsbyroleFn?.data?.roles || [];

  if (
    roles.includes(config.superUserRole) ||
    roles.includes("organization_admin") 
  ) {
    Object.assign(permissionsObj, {
      createfederation: "full",
      campaignplanning: "read",
      query: "full",
      provenancetrail: "read",
      activationhistory: "full",
      activationconnectors: "full",
      datamanager: "full",
    });
  } else {
    finalPermissions.forEach((perm) => {
      const [page, accessType] = perm.split("_");
      permissionsObj[page] = accessType;
    });
  }

  localStorage.setItem("userPermissions", JSON.stringify(permissionsObj));
  localStorage.setItem("userRole", roles);
  // Discover leads to Curation, which only platform admins may use (src/onam/access.ts)
  const curator = canCurate(authContext);

  useEffect(() => {
    const onPopState = () => {
      const state = (window.history.state as { console?: string }) || {};
      if (state.console) {
        setActiveConsole(state.console);
      } else {
        setActiveConsole(null);
      }
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("federationkey", JSON.stringify(null));
    localStorage.setItem("federationname", "");
    // FederationProvider state: set after render, not during it
    setFedId(null);
    setPlatformLogo(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const consoleKey = curator || (activeConsole || consoleParam) !== "Discover" ? activeConsole || consoleParam : null;

    if (consoleKey && subMenus[consoleKey]) {
      setCardstoShow(subMenus[consoleKey]);
    } else {
      setCardstoShow(mainCards);
    }

    if (consoleParam) setActiveConsole(consoleParam);
  }, [activeConsole, consoleParam, curator]);

  const { showToast } = useToast();
  // sent back here from a Curation page without the role
  useEffect(() => {
    const notice = (location.state as any)?.onamNotice;
    if (notice) { showToast(notice, "info"); history.replace({ pathname: location.pathname, search: location.search }); }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleMainCardClick = (title: string) => {
    if (mainCards.find((c) => c.title === title)?.text === "Coming soon") {
      showToast(`${title} is coming soon.`, "info");
      return;
    }
    if (title === "Discover" && !curator) {
      showToast(CURATION_DENIED, "info");
      return;
    }
    setActiveConsole(title);

    window.history.pushState({ console: title }, "", `?console=${title}`);
  };

  const handleSubCardClick = (sub: {
    title: string;
    text: string;
    route?: string;
  }) => {
    if (sub.text === "Coming soon") {
      showToast(`${sub.title} is coming soon.`, "info");
      return;
    }
    if (sub.route) {
      history.push(sub.route);
    }
  };

  function navigateToEntryHub() {
    setActiveConsole("");
    history.push("/entry-hub");
  }

  // ONAM look (src/onam/hub): the same consoles and routes, as cards
  const asked = activeConsole || consoleParam || null;
  const consoleName = asked === "Discover" && !curator ? null : asked;
  return (
    <OnamHub
      console={consoleName}
      greeting={authContext?.user?.given_name}
      onBack={navigateToEntryHub}
      cards={cardsToShow.map((card: any) => ({
        title: card.title,
        desc: card.desc || "",
        live: card.text !== "Coming soon" && !(card.title === "Discover" && !consoleName && !curator),
        cta: card.cta,
        state: card.title === "Discover" && !consoleName && !curator ? "Platform admins" : card.text || undefined,
        onClick: () =>
          consoleName ? handleSubCardClick(card) : handleMainCardClick(card.title),
      }))}
    />
  );
};

export default Orbit2;
